import React, { useState, useEffect, useRef } from 'react'
import api from '../../utils/api'
import IntegrationsOverview from '../../components/company/IntegrationsOverview'
import CompanyTabs from '../../components/company/CompanyTabs'
import { getPeriodDateRange } from '../../utils/statsPeriod'

const companyLabel = (company) => company.nome_fantasia || company.razao_social

// associatedCompanies is only filled when the logged company is a núcleo; it turns the page into a
// tabbed view (first tab = the logged company, then one tab per associated company). A company that
// is not a núcleo gets no tabs and only ever sees its own data.
// The selected tab is owned by the dashboard (activeCompanyId / onActiveCompanyChange) so it survives
// drilling into the traces/errors pages and coming back. The drill-down handlers read the dashboard's
// active company, so they work the same for the logged company and for an associated one.
const CompanyHomePage = ({
  companyId,
  companyName,
  associatedCompanies = [],
  activeCompanyId: selectedCompanyId = companyId,
  onActiveCompanyChange,
  integrations,
  onOpenTracesPage,
  onOpenErrorsPage
}) => {
  const [stats, setStats] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [period, setPeriod] = useState('today')
  const requestIdRef = useRef(0) // drops responses that arrive after the user already switched tab/period

  const hasTabs = associatedCompanies.length > 0
  // Falls back to the logged company if the selected tab disappeared (e.g. the association was removed)
  const activeCompanyId =
    selectedCompanyId === companyId || associatedCompanies.some((c) => c.id === selectedCompanyId)
      ? selectedCompanyId
      : companyId
  const isOwnTab = activeCompanyId === companyId
  const activeCompany = associatedCompanies.find((c) => c.id === activeCompanyId)

  const tabs = [
    { id: companyId, label: companyName || 'Minha empresa', icon: 'hub', title: 'Núcleo (empresa logada)' },
    ...associatedCompanies.map((c) => ({
      id: c.id,
      label: companyLabel(c),
      icon: 'apartment',
      title: c.nome_fantasia ? `${c.nome_fantasia} - ${c.razao_social}` : c.razao_social,
      inactive: !c.active
    }))
  ]

  const loadStats = async ({ silent = false, forPeriod = period } = {}) => {
    if (!activeCompanyId) return

    const requestId = ++requestIdRef.current
    if (silent) setRefreshing(true)
    else setLoading(true)
    setLoadError(false)

    try {
      // The dashboard already shows its own skeleton/spin-icon feedback for this
      // call (see IntegrationsOverview), so it opts out of the blocking global overlay.
      const { dateFrom, dateTo } = getPeriodDateRange(forPeriod)
      const response = await api.get(`/api/cloud-event-logs/company/${activeCompanyId}/stats`, {
        silentLoading: true,
        params: dateFrom && dateTo ? { date_from: dateFrom, date_to: dateTo } : undefined
      })
      if (requestId !== requestIdRef.current) return

      // Backend returns { integrations: [...] }
      const statsData = response.data?.integrations || []
      const validStats = Array.isArray(statsData) ? statsData : []
      setStats(validStats)
    } catch (error) {
      if (requestId !== requestIdRef.current) return
      console.error('Error loading stats:', error)
      setStats([]) // Set empty array on error
      setLoadError(true)
    } finally {
      if (requestId === requestIdRef.current) {
        if (silent) setRefreshing(false)
        else setLoading(false)
      }
    }
  }

  useEffect(() => {
    loadStats({ forPeriod: period })
  }, [activeCompanyId, period])

  const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}')

  return (
    <div className="w-full">
      {hasTabs && (
        <CompanyTabs tabs={tabs} activeId={activeCompanyId} onChange={(id) => onActiveCompanyChange?.(id)} />
      )}

      {hasTabs && !isOwnTab && activeCompany && (
        <p className="flex items-center flex-wrap gap-x-1.5 gap-y-1 text-xs text-gray-500 dark:text-gray-400 mb-3">
          <span className="material-icons" style={{ fontSize: '14px' }}>visibility</span>
          <span>
            Visualizando as integrações de <span className="font-semibold text-gray-700 dark:text-gray-200">{companyLabel(activeCompany)}</span>
          </span>
        </p>
      )}

      {loadError && !isOwnTab && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg text-xs bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
          <span className="material-icons" style={{ fontSize: '16px' }}>error</span>
          Não foi possível carregar os dados desta empresa.
        </div>
      )}

      {/* Integrations overview. An associated company's cards are built from the stats payload itself (name,
          supplier, logo), so its tab needs no integration list; only the logged company passes its own. */}
      <IntegrationsOverview
        companyId={activeCompanyId}
        stats={stats}
        loading={loading}
        refreshing={refreshing}
        onRefresh={() => loadStats({ silent: true })}
        username={userInfo.username}
        integrations={isOwnTab ? integrations : []}
        onOpenTracesPage={onOpenTracesPage}
        onOpenErrorsPage={onOpenErrorsPage}
        period={period}
        onPeriodChange={setPeriod}
      />
    </div>
  )
}

export default CompanyHomePage
